import { Box, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAuth } from '@/auth/useAuth'
import { useContext } from '@/hooks/useContext'

interface ContextSwitchOverlayProps {
  isVisible: boolean
  newContextName: string | null
  /** Same blur shell; impersonation uses shorter copy (no circle / “from → to”). */
  variant?: 'context' | 'impersonation'
}

export const ContextSwitchOverlay = ({
  isVisible,
  newContextName,
  variant = 'context',
}: ContextSwitchOverlayProps) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { contextName } = useContext()
  const [displayName, setDisplayName] = useState<string | null>(null)

  /** Avoid t('context.user') without `name` (would show raw "{{name}}" in copy). */
  const personalIdentityLabel = useMemo(
    () =>
      t('context.user', {
        name: user?.firstname?.trim() ? user.firstname : '\u2026',
      }),
    [t, user?.firstname]
  )

  useEffect(() => {
    if (isVisible && variant === 'context') {
      // Get the target name from localStorage if available
      const switchingTo = localStorage.getItem('switchingTo')
      setDisplayName(switchingTo || newContextName)
    } else {
      setDisplayName(null)
    }
  }, [isVisible, newContextName, variant])

  if (!isVisible) return null

  return (
    <Portal>
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bg="blackAlpha.600"
        zIndex={99999}
        display="flex"
        alignItems="center"
        justifyContent="center"
        backdropFilter="blur(8px)"
      >
        <VStack
          bg="white"
          _dark={{ bg: 'gray.800' }}
          p={8}
          borderRadius="lg"
          boxShadow="xl"
          gap={4}
          minW="300px"
        >
          <Spinner size="xl" colorPalette="brand" />
          {variant === 'impersonation' ? (
            <>
              <Text fontSize="lg" fontWeight="medium" textAlign="center">
                {t('context.impersonationOverlayTitle')}
              </Text>
              <Text fontSize="sm" color="fg.muted" textAlign="center">
                {t('context.impersonationOverlayDescription')}
              </Text>
            </>
          ) : (
            <>
              <Text fontSize="lg" fontWeight="medium" textAlign="center">
                {t('context.switching', {
                  from: contextName || personalIdentityLabel,
                  to: displayName || personalIdentityLabel,
                })}
              </Text>
              <Text fontSize="sm" color="fg.muted" textAlign="center">
                {displayName?.trim()
                  ? t('context.switchingDescription', { to: displayName })
                  : t('context.switchingDescriptionPending')}
              </Text>
            </>
          )}
        </VStack>
      </Box>
    </Portal>
  )
}
